import { api } from '@/lib/api/client';
import type {
  Bill,
  BillFilters,
  BillsResponse,
  BillsSummary,
  CreateBillDto,
  UpdateBillDto,
  CreateBillFromContractPeriodDto,
  CreateBillFromFixedDto,
  CreateBillFromSuccessFeeDto,
} from '../types';

const BILLS_URL = '/bills';

export const billsApi = {
  // Get all bills with filters
  getAll: async (filters?: BillFilters): Promise<BillsResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    return api.get<BillsResponse>(BILLS_URL, { params });
  },

  /**
   * Billed / paid roll-up for every bill matching the filters — one bucket per
   * currency. Takes the SAME query params as `getAll` minus paging, so the
   * numbers describe the whole filtered set, not the visible page.
   */
  getSummary: async (filters?: BillFilters): Promise<BillsSummary> => {
    const params: Record<string, unknown> = { ...filters };

    // Paging/sorting are meaningless for an aggregate — drop them so the query
    // key stays stable across page changes and the server can't be confused.
    delete params['page'];
    delete params['limit'];
    delete params['offset'];
    delete params['orderBy'];
    delete params['order'];

    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    return api.get<BillsSummary>(`${BILLS_URL}/summary`, { params });
  },

  // Get single bill by ID
  getById: async (id: number | string): Promise<Bill> => {
    return api.get<Bill>(`${BILLS_URL}/${id}`);
  },

  // Create new bill
  create: async (data: CreateBillDto): Promise<Bill> => {
    return api.post<Bill>(BILLS_URL, data);
  },

  // Update bill
  update: async ({ id, data }: { id: number | string; data: UpdateBillDto }): Promise<Bill> => {
    return api.patch<Bill>(`${BILLS_URL}/${id}`, data);
  },

  // Delete bill
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${BILLS_URL}/${id}`);
  },

  // Download bill as PDF
  download: async (id: number | string, format: string = 'pdf'): Promise<Blob> => {
    return api.get<Blob>(`${BILLS_URL}/download/${id}/${format}`, {
      responseType: 'blob',
    });
  },

  // Create bill from contract period
  createFromContractPeriod: async (data: CreateBillFromContractPeriodDto): Promise<Bill> => {
    return api.post<Bill>(`${BILLS_URL}/contract-period`, data);
  },

  // Create bill from fixed contract
  createFromFixed: async (data: CreateBillFromFixedDto): Promise<Bill> => {
    return api.post<Bill>(`${BILLS_URL}/fixed-contract`, data);
  },

  // Create bill from success fee contract
  createFromSuccessFee: async (data: CreateBillFromSuccessFeeDto): Promise<Bill> => {
    return api.post<Bill>(`${BILLS_URL}/success-fee-contract`, data);
  },

  // Accept bill (DRAFT → UNPAID)
  accept: async (id: number | string): Promise<Bill> => {
    return api.put<Bill>(`${BILLS_URL}/accept/${id}`, {});
  },

  // Archive bill
  archive: async (id: number | string): Promise<Bill> => {
    return api.put<Bill>(`${BILLS_URL}/archive/${id}`, {});
  },
};

// Query keys for React Query
export const billsQueryKeys = {
  all: ['bills'] as const,
  lists: () => [...billsQueryKeys.all, 'list'] as const,
  list: (filters?: BillFilters) => [...billsQueryKeys.lists(), filters] as const,
  summary: (filters?: BillFilters) => [...billsQueryKeys.all, 'summary', filters] as const,
  details: () => [...billsQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...billsQueryKeys.details(), id] as const,
};
